import type {
    KitsUITools,
    LegacyKitsUITools,
} from "@wags/concierge-agent-programs/kits/ui-tools.ts";
import type { UIMessage } from "ai";
import type { JsonValue } from "@wags/utils/json.ts";
import type { UserDeliveryPublicMessage } from "@wags/agent-contracts/user-delivery.contract.ts";
import type {
    LegacyProviderContactWidgetPartV1,
    LegacyQuickRepliesWidgetPartV1,
} from "./legacy-widget-parts.contract";

export type WagsTools = Omit<
    KitsUITools,
    "createProviderContactForm" | "createQuickReplies"
> & {
    createProviderContactForm: {
        input:
            | KitsUITools["createProviderContactForm"]["input"]
            | LegacyProviderContactWidgetPartV1["input"];
        output:
            | KitsUITools["createProviderContactForm"]["output"]
            | LegacyProviderContactWidgetPartV1["output"];
    };
    createQuickReplies: {
        input:
            | KitsUITools["createQuickReplies"]["input"]
            | LegacyQuickRepliesWidgetPartV1["input"];
        output:
            | KitsUITools["createQuickReplies"]["output"]
            | LegacyQuickRepliesWidgetPartV1["output"];
    };
} & LegacyKitsUITools;

export type WagsDataParts = Record<string, never>;

export type WagsWidgetInteraction = {
    toolCallId: string;
    widget: keyof WagsTools & string;
    payload: JsonValue;
};

export const CONTEXTUAL_ONBOARDING_TRIGGER_NAMES = [
    "first_save",
    "overwhelm",
    "dog_page",
    "far_dog",
] as const;

export type TriggerEvent = {
    name: (typeof CONTEXTUAL_ONBOARDING_TRIGGER_NAMES)[number];
};

export const EVENT_MARKER = "[event]";

export const EVENT_BLOCK = `\n\n${EVENT_MARKER}`;

export function newKitsInputId() {
    return `wags-web:${crypto.randomUUID()}`;
}

export const TRIGGER_PREFIX = `​${EVENT_MARKER}`;

export const STATUS_ONLY_TOOLS = [
    "get_user_facts",
    "save_user_facts",
    "get_breed_info",
    "compute_breed_fit",
    "get_question_bank",
    "get_listing",
] as const satisfies readonly (keyof WagsTools & string)[];

export type StatusOnlyTool = (typeof STATUS_ONLY_TOOLS)[number];

export type WagsMessageMetadata = {
    userDelivery?: UserDeliveryPublicMessage;
    streamPhase?: "draft" | "committed";
    streamTurnId?: string;
    conversationId?: string;
    responseId?: string;
    kitsMessageId?: string;
    interaction?: WagsWidgetInteraction;
    trigger?: TriggerEvent;

    canonicalInputId?: string;
    publicCursor?: number;
    interrupted?: boolean;
    createdAt?: string;
};

export type WagsUIMessage = UIMessage<
    WagsMessageMetadata,
    WagsDataParts,
    WagsTools
>;

export function isTriggerMessage(message: WagsUIMessage): boolean {
    if (message.metadata?.trigger) return true;
    return message.parts.some(
        (part) => part.type === "text" && part.text.startsWith(TRIGGER_PREFIX),
    );
}

export function readInteraction(
    message: WagsUIMessage,
): WagsWidgetInteraction | null {
    const interaction = message.metadata?.interaction;
    return interaction &&
        typeof interaction === "object" &&
        typeof interaction.toolCallId === "string" &&
        typeof interaction.widget === "string"
        ? interaction
        : null;
}

type WagsMessagePart = WagsUIMessage["parts"][number];

export function rawVisibleText(part: WagsMessagePart): string {
    if (part.type !== "text") return "";
    return part.text?.split(EVENT_BLOCK)[0] ?? "";
}

export function visibleText(part: WagsMessagePart): string {
    return rawVisibleText(part).trim();
}
